import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type CompanyInfo = {
  name: string;
  legal_name: string;
  tax_id: string;
  phone: string;
  email: string;
  website: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  currency_code: string;
  logo_url?: string;
  timezone?: string;
};

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['Company'],
  endpoints: (builder) => ({
    getCompany: builder.query<ApiResponseBase<CompanyInfo>, void>({
      query: () => ({ url: '/v1/onboarding/company', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Onboarding.GetCompany'),
      providesTags: ['Company'],
    }),
    updateCompany: builder.mutation<
      ApiResponseBase<null> | ApiResponseBase<unknown>,
      Partial<CompanyInfo> & { logo_base64?: string; timezone?: string }
    >({
      query: (body) => ({ url: '/v1/onboarding/company', method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Onboarding.UpdateCompany'),
      invalidatesTags: ['Company'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar la empresa',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Onboarding.UpdateCompany'}${e?.code ? ` — Codigo: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'onboarding.company.update',
              endpointName: e?.endpointName || 'Onboarding.UpdateCompany',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const { useGetCompanyQuery, useLazyGetCompanyQuery, useUpdateCompanyMutation } =
  onboardingApi;
